/**
 * @file useWebsites.ts
 * @description Custom hook for managing websites state.
 * Provides functionality to fetch, create, update, and delete websites with optional client filtering.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import { auth } from "../config/firebaseConfig";
import { Website } from "../types";
import {
  getWebsites as fetchWebsites,
  createWebsite as createWebsiteApi,
  updateWebsite as updateWebsiteApi,
  deleteWebsite as deleteWebsiteApi,
  CreateWebsiteData,
  UpdateWebsiteData,
} from "../services/websiteService";
import log from "../config/logging";

// Global flag to prevent duplicate error toasts
let hasShownErrorToast = false;
const ERROR_TOAST_RESET_DELAY = 5000; // Reset flag after 5 seconds

export const useWebsites = (clientId?: string) => {
  const [websites, setWebsites] = useState<Website[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasLoadedRef = useRef<string>(""); // Track last loaded clientId

  const loadWebsites = useCallback(async () => {
    // Check authentication before making request
    const user = auth.currentUser;
    if (!user) {
      log.debug("User not authenticated, skipping websites fetch");
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const data = await fetchWebsites(clientId);
      setWebsites(data);
      const cacheKey = clientId || "all";
      hasLoadedRef.current = cacheKey;
      hasShownErrorToast = false; // Reset error flag on success
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to load websites";
      setError(errorMessage);
      log.error("Error loading websites", { error: err });
      
      // Only show toast if we haven't shown one recently
      if (!hasShownErrorToast) {
        hasShownErrorToast = true;
        toast.error(errorMessage);
        // Reset flag after delay
        setTimeout(() => {
          hasShownErrorToast = false;
        }, ERROR_TOAST_RESET_DELAY);
      }
    } finally {
      setIsLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    // Wait for authentication before loading
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        const cacheKey = clientId || "all";
        if (hasLoadedRef.current !== cacheKey) {
          loadWebsites();
        }
      }
    });

    // Also try to load immediately if user is already authenticated
    if (auth.currentUser) {
      const cacheKey = clientId || "all";
      if (hasLoadedRef.current !== cacheKey) {
        loadWebsites();
      }
    }

    return () => unsubscribe();
  }, [loadWebsites, clientId]);

  const createWebsite = useCallback(
    async (websiteData: CreateWebsiteData) => {
      try {
        setIsLoading(true);
        setError(null);
        const websiteId = await createWebsiteApi(websiteData);
        await loadWebsites();
        toast.success("Sito web creato con successo");
        return websiteId;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to create website";
        setError(errorMessage);
        log.error("Error creating website", { error: err });
        toast.error(errorMessage);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [loadWebsites]
  );

  const updateWebsite = useCallback(
    async (websiteId: string, websiteData: UpdateWebsiteData) => {
      try {
        setIsLoading(true);
        setError(null);
        await updateWebsiteApi(websiteId, websiteData);
        await loadWebsites();
        toast.success("Sito web aggiornato con successo");
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to update website";
        setError(errorMessage);
        log.error("Error updating website", { error: err });
        toast.error(errorMessage);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [loadWebsites]
  );

  const deleteWebsite = useCallback(
    async (websiteId: string) => {
      try {
        setIsLoading(true);
        setError(null);
        await deleteWebsiteApi(websiteId);
        await loadWebsites();
        toast.success("Sito web eliminato con successo");
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to delete website";
        setError(errorMessage);
        log.error("Error deleting website", { error: err });
        toast.error(errorMessage);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [loadWebsites]
  );

  return {
    websites,
    isLoading,
    error,
    loadWebsites,
    createWebsite,
    updateWebsite,
    deleteWebsite,
  };
};
